import { useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eraser, PenLine, Type } from "lucide-react";

// Reusable signature capture: draw on canvas or type a name.
// Calls onChange({ name, mode, image }) whenever the signature changes.
export const SignaturePad = ({ onChange, testidPrefix = "sig" }) => {
  const [mode, setMode] = useState("type");
  const [name, setName] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  const emit = (nextName, nextMode) => {
    let image = "";
    if (nextMode === "draw" && canvasRef.current && hasInk.current) {
      image = canvasRef.current.toDataURL("image/png");
    }
    onChange?.({ name: nextName, mode: nextMode, image });
  };

  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a1a";
  }, [mode]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emit(name, "draw");
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    emit(name, "draw");
  };

  const switchMode = (m) => {
    setMode(m);
    emit(name, m);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>Full legal name</Label>
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            emit(e.target.value, mode);
          }}
          placeholder="Type your full name"
          data-testid={`${testidPrefix}-name-input`}
        />
      </div>

      <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
        <button
          type="button"
          onClick={() => switchMode("type")}
          className={`text-sm px-3 py-1.5 rounded-md flex items-center gap-1.5 ${
            mode === "type" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
          }`}
          data-testid={`${testidPrefix}-mode-type`}
        >
          <Type className="h-3.5 w-3.5" /> Type
        </button>
        <button
          type="button"
          onClick={() => switchMode("draw")}
          className={`text-sm px-3 py-1.5 rounded-md flex items-center gap-1.5 ${
            mode === "draw" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
          }`}
          data-testid={`${testidPrefix}-mode-draw`}
        >
          <PenLine className="h-3.5 w-3.5" /> Draw
        </button>
      </div>

      {mode === "type" ? (
        <div className="border border-border rounded-lg h-24 flex items-center px-4 bg-card">
          <span className="text-3xl italic text-foreground" style={{ fontFamily: "cursive" }}>
            {name || "Your signature"}
          </span>
        </div>
      ) : (
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={460}
            height={120}
            className="border border-border rounded-lg bg-card w-full touch-none cursor-crosshair"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            data-testid={`${testidPrefix}-canvas`}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1 h-7"
            onClick={clearCanvas}
            data-testid={`${testidPrefix}-clear`}
          >
            <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        </div>
      )}
    </div>
  );
};
